import React, { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AuthProvider } from '@/platform/auth/AuthContext'
import { ProtectedRoute } from '@/platform/auth/ProtectedRoute'
import { AppShell } from '@/platform/shell/AppShell'
import { ModuleLoader } from '@/platform/modules/ModuleLoader'
import { LoginPage } from '@/platform/auth/pages/LoginPage'
import { RegisterPage } from '@/platform/auth/pages/RegisterPage'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { PublicLayout } from '@/platform/shell/PublicLayout'
import { PrivacyPage } from '@/platform/compliance/PrivacyPage'
import { TermsPage } from '@/platform/compliance/TermsPage'
import { DataDeletionPage } from '@/platform/compliance/DataDeletionPage'
import { ContactPage } from '@/platform/compliance/ContactPage'
import { lazyWithRetry } from '@/shared/utils/lazyWithRetry'

const PlaygroundPage = lazyWithRetry(() => import('@/platform/experiences/playground/PlaygroundPage'))
const GitaModule = lazyWithRetry(() => import('@/features/gita/GitaModule'))

/**
 * TanStack Query client configuration.
 *
 * Global defaults:
 * - staleTime: 0 — data is considered stale immediately after fetch (default)
 * - retry: 1 — retry failed requests once before surfacing error
 * - refetchOnWindowFocus: true — re-validates data when user returns to tab
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 0,
    },
    mutations: {
      retry: 0,
    },
  },
})

import { LandingPage } from '@/platform/landing/LandingPage'

/**
 * Root application component.
 *
 * Provider hierarchy (innermost wins):
 * QueryClientProvider → AuthProvider → Router → Routes
 *
 * Route structure:
 *   /            → LandingPage (public)
 *   /login       → LoginPage (public)
 *   /register    → RegisterPage (public)
 *   /privacy     → PrivacyPage (public, wrapped in PublicLayout)
 *   /terms       → TermsPage (public, wrapped in PublicLayout)
 *   /data-deletion → DataDeletionPage (public, wrapped in PublicLayout)
 *   /contact     → ContactPage (public, wrapped in PublicLayout)
 *   /            → AppShell + ModuleLoader (protected)
 *     /todo      → Todo module (Phase 2)
 *     /habits    → Habits module (Phase N)
 *     …          → Any future module
 */
export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route
                path="/gita/*"
                element={
                  <ErrorBoundary moduleName="Gita">
                    <Suspense fallback={<div style={{ padding: '24px' }}>Loading Gita Experience...</div>}>
                      <GitaModule />
                    </Suspense>
                  </ErrorBoundary>
                }
              />

              {/* Public Compliance Routes wrapped in PublicLayout */}
              <Route element={<PublicLayout />}>
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/data-deletion" element={<DataDeletionPage />} />
                <Route path="/contact" element={<ContactPage />} />
              </Route>

              {/* Shell layout structure */}
              <Route element={<AppShell />}>

                {/* Protected routes inside AppShell */}
                <Route
                  element={
                    <ProtectedRoute>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  {/* Developer Playground route */}
                  <Route
                    path="playground"
                    element={
                      <ErrorBoundary moduleName="Playground">
                        <Suspense fallback={<div style={{ padding: '24px' }}>Loading Playground...</div>}>
                          <PlaygroundPage />
                        </Suspense>
                      </ErrorBoundary>
                    }
                  />
                  {/* Module routes rendered inside AppShell's <Outlet /> */}
                  <Route path="*" element={<ModuleLoader />} />
                </Route>
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>

        {/* React Query Devtools — dev only (excluded from production build) */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
